'use client';

import { useEffect, useRef } from 'react';

import { useTranslations } from 'use-intl';

import { MAX_RESPONSE_LENGTH } from '@/shared-question-model';
import {
  SharedSurveyVoiceInstruction,
  useIsAiAudioActive,
} from '@/shared-survey';
import { SharedUiComposer } from '@/shared-ui';
import { useIsDesktop } from '@/shared-utilities';
import { VoiceWaveform } from '@/ui';

import { ProjectResponseSendComponentProps } from './project-response-renderer.type';
import { useProjectResponseSend } from './project-response-send.hooks';
import { resolveRenderMobileView } from './project-response-voice-send.helpers';

const MAX_RECORDING_MS = 5 * 60 * 1000;

const DESKTOP_BREAKPOINT = 640;

export default function ProjectResponseVoiceSend({
  isSubmitting,
  disabled,
  isConversationOver,
  type,
  isUserVoiceEnabled,
  renderMobileView,
}: ProjectResponseSendComponentProps) {
  const tChat = useTranslations('Chat');
  const tVoice = useTranslations('Voice');

  const transcriptRef = useRef<HTMLTextAreaElement>(null);

  const isAiAudioActive = useIsAiAudioActive();

  const { isDesktop, mounted } = useIsDesktop(DESKTOP_BREAKPOINT);

  const {
    isListening,
    audioLevelObservable,
    AUDIO_AMPLIFIER,
    handleToggle,
    currentTranscript,
    isStarting,
    manuallyEditTranscript,
    startDateOfTranscript,
    stopListening,
  } = useProjectResponseSend(type);

  const showWaveform =
    (isListening || isStarting) && !isAiAudioActive && !isSubmitting;
  const shouldRenderMobileView = resolveRenderMobileView(
    renderMobileView,
    isDesktop || !mounted
  );

  useEffect(() => {
    const intervalId = setInterval(() => {
      const startDate = startDateOfTranscript();
      if (!startDate) return;
      if (Date.now() - startDate > MAX_RECORDING_MS) {
        void stopListening();
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [startDateOfTranscript, stopListening]);

  useEffect(() => {
    if (transcriptRef.current && currentTranscript) {
      transcriptRef.current.scrollTo({
        top: transcriptRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [currentTranscript]);

  if (!isUserVoiceEnabled) {
    return null;
  }

  const getPlaceholder = () => {
    if (isConversationOver) return tVoice('finished');
    if (isSubmitting) return tChat('thinking');
    if (isAiAudioActive) return tVoice('aiSpeaking');
    return undefined;
  };

  const size = shouldRenderMobileView ? 'compact' : 'default';
  const waveformHeight = shouldRenderMobileView ? 24 : 32;
  const submitLabel = isListening ? tVoice('continue') : tChat('next');

  return (
    <SharedUiComposer
      size={size}
      type={showWaveform ? 'voice' : 'write'}
      data-test={
        shouldRenderMobileView ? 'voice-send-mobile' : 'voice-send-desktop'
      }
      submitType='submit'
      value={currentTranscript}
      onChange={(next) =>
        manuallyEditTranscript(next.slice(0, MAX_RESPONSE_LENGTH))
      }
      placeholder={getPlaceholder()}
      maxLength={MAX_RESPONSE_LENGTH}
      textareaRef={transcriptRef}
      textareaAriaLabel={tVoice('transcriptAriaLabel')}
      textareaDisabled={isConversationOver || isSubmitting}
      waveformSlot={
        <VoiceWaveform
          isListening={showWaveform && !isConversationOver}
          audioLevelSource={audioLevelObservable}
          audioLevelMultiplier={AUDIO_AMPLIFIER}
          gradientReversed
          barWidth={3}
          height={waveformHeight}
          className={shouldRenderMobileView ? 'h-6 w-full' : 'h-8 w-full'}
        />
      }
      canSubmit={!disabled && !isConversationOver}
      loading={isSubmitting}
      submitLabel={submitLabel}
      submitAriaLabel={submitLabel}
      submitClassName='bg-(--brand-primary) text-(--brand-primary-contrast) [&_svg]:text-(--brand-primary-contrast) enabled:hover:bg-(--brand-primary) disabled:bg-(--brand-primary) disabled:opacity-20'
      isVoiceEnabled
      onToggleVoice={handleToggle}
      voiceToggleDisabled={isConversationOver || isSubmitting}
      voiceToggleAriaLabel={tVoice('cancelRecordingAria')}
      footerStart={
        shouldRenderMobileView ? null : <SharedSurveyVoiceInstruction />
      }
    />
  );
}
